import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Network } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";

const emptyForm = { name: "", branch_ids: [], description: "" };

export default function Departments({ embedded = false }) {
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterBranch, setFilterBranch] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterBranch !== "all") params.branch_id = filterBranch;
      const [deptRes, brRes] = await Promise.all([
        api.get("/departments", { params }),
        api.get("/branches"),
      ]);
      setDepartments(deptRes.data);
      setBranches(brRes.data);
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [filterBranch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getBranchNames = (ids) => {
    if (!ids || !Array.isArray(ids) || ids.length === 0) return "\u2014";
    return ids
      .map((id) => branches.find((b) => b.id === id)?.name)
      .filter(Boolean)
      .join(", ") || "\u2014";
  };

  const toggleBranch = (branchId) => {
    setForm((prev) => {
      const ids = prev.branch_ids || [];
      return {
        ...prev,
        branch_ids: ids.includes(branchId)
          ? ids.filter((id) => id !== branchId)
          : [...ids, branchId],
      };
    });
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditId(d.id);
    setForm({
      name: d.name,
      branch_ids: d.branch_ids || (d.branch_id ? [d.branch_id] : []),
      description: d.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.branch_ids || form.branch_ids.length === 0) {
      toast.error("Nombre y al menos una sucursal son requeridos");
      return;
    }
    try {
      if (editId) {
        await api.put(`/departments/${editId}`, form);
        toast.success("Departamento actualizado");
      } else {
        await api.post("/departments", form);
        toast.success("Departamento creado");
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Error al guardar departamento");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este departamento?")) return;
    try {
      await api.delete(`/departments/${id}`);
      toast.success("Departamento eliminado");
      fetchData();
    } catch {
      toast.error("Error al eliminar departamento");
    }
  };

  return (
    <div data-testid="departments-page">
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Departamentos
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Organizacion departamental por sucursal
            </p>
          </div>
        )}
        {embedded && <div />}
        <Button
          onClick={openCreate}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="add-department-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo departamento
        </Button>
      </div>

      <div className="mb-6">
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-[200px]" data-testid="department-filter-branch">
            <SelectValue placeholder="Filtrar por sucursal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las sucursales</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Nombre
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Sucursal
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Descripcion
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Estado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {departments.map((d) => (
                <TableRow
                  key={d.id}
                  className="border-zinc-100"
                  data-testid={`department-row-${d.id}`}
                >
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {d.name}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">
                    {getBranchNames(d.branch_ids || (d.branch_id ? [d.branch_id] : []))}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden sm:table-cell">
                    {d.description || "\u2014"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        d.active !== false
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 font-medium"
                          : "bg-zinc-100 text-zinc-500 border-zinc-200 font-medium"
                      }
                    >
                      {d.active !== false ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(d)}
                      data-testid={`edit-department-${d.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(d.id)}
                      data-testid={`delete-department-${d.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {departments.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-16 text-zinc-400"
                  >
                    <Network className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay departamentos registrados</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editId ? "Editar departamento" : "Nuevo departamento"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Nombre *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="department-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Sucursales *
              </Label>
              {branches.length === 0 ? (
                <p className="text-sm text-zinc-400 mt-2">No hay sucursales registradas</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {branches.map((b) => (
                    <label
                      key={b.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-md border border-zinc-200 hover:bg-zinc-50 cursor-pointer transition-colors"
                      data-testid={`department-branch-checkbox-${b.id}`}
                    >
                      <Checkbox
                        checked={(form.branch_ids || []).includes(b.id)}
                        onCheckedChange={() => toggleBranch(b.id)}
                      />
                      <span className="text-sm text-zinc-900">{b.name}</span>
                      {b.code && (
                        <span className="text-xs text-zinc-400 font-mono ml-auto">{b.code}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Descripcion
              </Label>
              <Input
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                data-testid="department-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="department-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="department-submit-button"
            >
              {editId ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
